import React, { useState } from 'react';
import axios from 'axios';
import { Bell, Check } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API = '';

const capitalize = (str) => {
  if (!str) return '';
  return str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const SaveSearchButton = ({ searchType, category, town, keywords, hasFilters }) => {
  const { user } = useAuth();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!hasFilters) return null;

  const typeLabel = searchType === 'classified' ? 'Marketplace' : capitalize(searchType);
  const displayTown = town === 'uk-wide' ? 'UK-wide' : town;
  const buildLabel = () => {
    const parts = [];
    if (category) parts.push(capitalize(category));
    parts.push(typeLabel);
    parts.push(displayTown ? `in ${capitalize(displayTown)}` : 'in Essex');
    return parts.join(' ');
  };
  const label = buildLabel();

  const handleClick = () => {
    if (!user) {
      window.location.href = '/signup?next=dashboard';
      return;
    }
    setShowConfirm(true);
  };

  const handleSave = async () => {
    if (saved) return;
    setSaving(true);
    setShowConfirm(false);
    try {
      await axios.post(`${API}/api/saved-searches`, {
        search_type: searchType,
        category: category || null,
        town: displayTown || null,
        keywords: keywords || null,
        label,
      });
      setSaved(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save search');
    } finally {
      setSaving(false);
    }
  };

  // Saved confirmation state
  if (saved) {
    return (
      <div
        className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 w-full"
        data-testid="save-search-saved-banner"
      >
        <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
        <span>Alert saved — we'll notify you when new matches appear.</span>
        <Link to="/dashboard?tab=account" className="ml-auto text-xs text-green-700 font-semibold hover:text-green-900 underline underline-offset-2 whitespace-nowrap">
          Manage alerts
        </Link>
      </div>
    );
  }

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={saving}
        className="gap-1.5 text-[#0F2C3E] border-slate-300 hover:bg-[#0F2C3E] hover:text-white hover:border-[#0F2C3E] transition-colors"
        data-testid="save-search-btn"
      >
        <Bell className="h-3.5 w-3.5" />
        {saving ? 'Saving…' : 'Save Search & Get Alerts'}
      </Button>

      {showConfirm && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setShowConfirm(false)} />
          <div className="absolute right-0 top-full mt-2 z-50 w-80 bg-white rounded-lg shadow-xl border p-4" data-testid="save-search-confirm-dialog">
            <h4 className="font-semibold text-slate-800 text-sm mb-2">Save This Search?</h4>
            <p className="text-sm text-slate-600 mb-1">
              You'll receive email alerts when new <strong>{label.toLowerCase()}</strong> listings are added.
            </p>
            <p className="text-xs text-slate-400 mb-4">
              Manage or remove saved searches anytime in <strong>Dashboard &gt; Account</strong>.
            </p>
            <div className="flex gap-2">
              <Button size="sm" className="flex-1 bg-[#0F2C3E] hover:bg-[#0a1f2c] text-white" onClick={handleSave} disabled={saving} data-testid="confirm-save-search">
                {saving ? 'Saving…' : 'Yes, Save & Get Alerts'}
              </Button>
              <Button size="sm" variant="outline" className="flex-1" onClick={() => setShowConfirm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SaveSearchButton;
